/**
 * SubscriptionManagementView Component
 * User's subscription dashboard - current plan, usage, and management
 *
 * Pattern: Mobile-first card stack within max-w-md container
 * Design: Shows current tier, usage tracking, benefits, and upgrade CTAs
 */

import { Container, Card, Button, Stack, Badge } from '@talktoast/ui'
import { useSubscription } from '@talktoast/shared'
import { SubscriptionBadge } from './SubscriptionBadge'
import { UsageProgressBar } from './UsageProgressBar'

interface SubscriptionManagementViewProps {
  onBack: () => void
  onUpgrade: () => void
}

export function SubscriptionManagementView({ onBack, onUpgrade }: SubscriptionManagementViewProps) {
  const { subscription, usage, loading, openPlatformPortal } = useSubscription()

  const handleOpenPortal = async () => {
    try {
      await openPlatformPortal()
    } catch (error) {
      console.error('[SubscriptionManagementView] Failed to open platform portal:', error)
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <Container maxWidth="md" padding="md">
        <div className="space-y-4 animate-pulse">
          <div className="h-32 bg-gray-200 rounded-lg"></div>
          <div className="h-48 bg-gray-200 rounded-lg"></div>
          <div className="h-64 bg-gray-200 rounded-lg"></div>
        </div>
      </Container>
    )
  }

  if (!subscription) {
    return (
      <Container maxWidth="md" padding="md">
        <Card variant="elevated" padding="md">
          <p className="text-red-700 text-sm text-center">
            Unable to load subscription data. Please try again.
          </p>
        </Card>
      </Container>
    )
  }

  return (
    <Container maxWidth="md" padding="md">
      {/* Current Plan Summary */}
      <Card variant="elevated" padding="md" className="mb-3">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">Your Plan</h2>
            <Stack direction="horizontal" gap={2} align="center">
              <SubscriptionBadge tier={subscription.tier} size="md" />
              {subscription.isInTrial && (
                <Badge variant="warning" size="sm">
                  Trial · {subscription.daysUntilRenewal} days left
                </Badge>
              )}
            </Stack>
          </div>
          {subscription.tier === 'free' && (
            <Button variant="primary" size="sm" onClick={onUpgrade}>
              Upgrade
            </Button>
          )}
        </div>

        {subscription.isPaidTier && (
          <p className="text-sm text-gray-600">
            {subscription.isInTrial
              ? `Trial ends ${formatDate(subscription.trialEnd)}`
              : `Renews ${formatDate(subscription.periodEnd)}`}
          </p>
        )}
      </Card>

      {/* Usage Tracking */}
      {usage && (
        <Card variant="elevated" padding="md" className="mb-3">
          <h3 className="text-base font-bold text-gray-900 mb-3">Usage This Period</h3>
          <Stack direction="vertical" gap={3}>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Meal Plans</span>
                <span className="text-sm text-gray-600">
                  {subscription.tier === 'free'
                    ? `${usage.usage.mealPlansThisMonth}/${usage.limits.mealPlansPerMonth} this month`
                    : 'Unlimited'}
                </span>
              </div>
              {subscription.tier === 'free' && (
                <UsageProgressBar
                  current={usage.usage.mealPlansThisMonth}
                  limit={usage.limits.mealPlansPerMonth || 5}
                  label=""
                />
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">AI Chat Messages</span>
                <span className="text-sm text-gray-600">
                  {usage.limits.globalChatPerWeek === null
                    ? 'Unlimited'
                    : `${usage.usage.globalChatThisWeek}/${usage.limits.globalChatPerWeek} this week`}
                </span>
              </div>
              {usage.limits.globalChatPerWeek !== null && (
                <UsageProgressBar
                  current={usage.usage.globalChatThisWeek}
                  limit={usage.limits.globalChatPerWeek}
                  label=""
                />
              )}
            </div>
          </Stack>
        </Card>
      )}

      {/* Your Benefits */}
      <Card variant="elevated" padding="md" className="mb-3">
        <h3 className="text-base font-bold text-gray-900 mb-3">Your Benefits</h3>
        <Stack direction="vertical" gap={2}>
          {/* Meal plans */}
          <div className="flex items-center gap-2 text-sm">
            <svg className="w-5 h-5 text-avocado-green flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-gray-700">
              {subscription.tier === 'free' ? '5 meal plans/month' : 'Unlimited meal plans'}
            </span>
          </div>

          {/* Cuisines */}
          <div className="flex items-center gap-2 text-sm">
            <svg className="w-5 h-5 text-avocado-green flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-gray-700">
              {subscription.tier === 'free' ? '5 cuisines' : '18+ cuisines'}
            </span>
          </div>

          {/* Calorie targeting */}
          <div className="flex items-center gap-2 text-sm">
            {subscription.tier !== 'free' ? (
              <>
                <svg className="w-5 h-5 text-avocado-green flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700">Calorie targeting</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5 text-gray-300 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-400 line-through">Calorie targeting</span>
              </>
            )}
          </div>

        </Stack>
      </Card>

      {/* Platform Management */}
      {subscription.platform && (
        <Card variant="elevated" padding="md" className="mb-3">
          <h3 className="text-base font-bold text-gray-900 mb-2">Manage Subscription</h3>
          <p className="text-sm text-gray-600 mb-3">
            Your subscription is managed by {subscription.platform === 'stripe' ? 'Stripe' : subscription.platform === 'apple' ? 'Apple' : 'Google'}.
            You can update your payment method, view billing history, and cancel your subscription.
          </p>
          <Button
            variant="secondary"
            fullWidth
            onClick={handleOpenPortal}
            iconAfter={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            }
          >
            Open {subscription.platform === 'stripe' ? 'Stripe' : subscription.platform === 'apple' ? 'App Store' : 'Google Play'} Portal
          </Button>
        </Card>
      )}

      {/* Upgrade CTA (for Free users only) */}
      {subscription.tier === 'free' && (
        <Card variant="gradient" gradientColor="gold" padding="md">
          <h3 className="text-lg font-bold text-white mb-1">
            Upgrade to Premium
          </h3>
          <p className="text-sm text-white/90 mb-3">
            Unlock advanced features and save time
          </p>
          <Stack direction="vertical" gap={1} className="mb-3">
            <div className="flex items-center gap-2 text-sm text-white">
              <span>Unlimited meal plans</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-white">
              <span>18+ cuisines</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-white">
              <span>Calorie targeting</span>
            </div>
          </Stack>
          <Button variant="secondary" fullWidth onClick={onUpgrade}>
            View Plans
          </Button>
        </Card>
      )}
    </Container>
  )
}
